import React, { ReactElement, useMemo, useState, useEffect } from "react"

import { Loading, TextInput, InputGroup, RadioButton } from "@wfp/ui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Select from "react-select"
import { AutoSizer, List, ListRowProps } from "react-virtualized"

import { faRepeat } from "@fortawesome/free-solid-svg-icons"
import { useParams } from "react-router-dom"
import { useDataModaUploadContext, IFormFieldList } from "../../../providers/DataModaUploadContext"
import { MappingPriceOptionItem } from "../../../interfaces/mappingInterfaces"
import { useGetMSByIdQuery } from "../../../services/marketprices/mappingSchemas"

import { mappingOptions } from "../../../constants/prices"
import { TypeFilter } from "../../../providers/XslMappingContext"

const ROW_HEIGHT = 72
const DATE_TYPE_VALUES = [14, 16, 33]

const ModaFieldMapping = (): ReactElement => {
  const { sId } = useParams<{ sId: string }>()

  const {
    modaForm,
    modaFormFields,
    modaFormFieldsLoading,
    modaFormFieldsError,
    commodities,
    updateSchema,
    schemaId,
    isLoading,
    selectedOptions,
    xlsFormId,
    setXlsFormId,
    fetchModaFormFields,
    setCountry,
    setCommonSchema,
  } = useDataModaUploadContext()

  const { data: mappingSchema, isSuccess } = useGetMSByIdQuery(Number(sId), {
    skip: !sId,
  })

  const transformWithGroups = () => {
    const transformedFields: IFormFieldList[] = []
    modaFormFields.map((item, index) => {
      if (index <= 0 || modaFormFields[index - 1]?.groupName !== modaFormFields[index]?.groupName) {
        transformedFields.push({
          ...item,
          fieldIndex: null,
          groupName: item.groupName,
          fieldMetadata: "group",
        })
      }
      transformedFields.push(item)
    })
    return transformedFields
  }

  const [showCommodities, setShowCommodities] = useState<number[]>([])
  const [showUnits, setShowUnits] = useState<number[]>([])

  const [searchValue, setSearchValue] = useState<string>("")
  const [searchGroupValue, setSearchGroupValue] = useState<string>("")
  const [filterType, setFilterType] = useState<TypeFilter>(TypeFilter.ALL)

  const modaMappingOptions = useMemo(
    () =>
      mappingOptions
        .filter(option => {
          return option.label !== "Date"
        })
        .map(opt => ({
          ...opt,
          options: opt.options.map(option => {
            return {
              ...option,
              isDisabled: selectedOptions?.some(selectedOpt => {
                // Case of specific commodity mapping
                if (option?.value === "priceCommodityMeasure") {
                  return false
                }
                if (option?.value === "unitCommodity") {
                  return true
                }
                // default
                return selectedOpt.selected && selectedOpt?.type === option.type
              }),
            }
          }),
        })),
    [mappingOptions, selectedOptions],
  )

  const filteredFields = useMemo(() => {
    const fieldWithGroups = transformWithGroups()
    const ifGroupedSelection = searchGroupValue?.length
      ? fieldWithGroups.filter(field => {
          return field.groupName === searchGroupValue
        })
      : fieldWithGroups
    const query = searchValue.trim().toLowerCase()
    if (filterType === TypeFilter.MAPPED) {
      const mappedFields = ifGroupedSelection.filter(field => field.assignedTo)
      if (query.length < 3) return mappedFields
      return mappedFields.filter(field => field.fieldName.toLowerCase().includes(query))
    }
    if (filterType === TypeFilter.UNMAPPED) {
      const unmappedFields = ifGroupedSelection.filter(field => !field.assignedTo)
      if (query.length < 3) return unmappedFields
      return unmappedFields.filter(field => field.fieldName.toLowerCase().includes(query))
    }
    if (query.length < 3) return ifGroupedSelection
    return ifGroupedSelection.filter(field => field.fieldName.toLowerCase().includes(query))
  }, [searchValue, searchGroupValue, modaFormFields, filterType])

  const allGroup = useMemo(() => {
    return transformWithGroups()
      .filter(field => field.fieldMetadata === "group" || field.fieldMetadata === "root")
      .map(item => ({
        label: item.groupName,
        value: item.groupName,
      }))
  }, [modaFormFields])

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value as TypeFilter
    setFilterType(value)
  }

  const getDefaultTypeOptions = (dataType: string): MappingPriceOptionItem | null | undefined => {
    const values = mappingOptions
      .map(option =>
        option.options.find(opt => {
          return opt?.value === dataType
        }),
      )
      .filter(item => !!item)
    return values.length ? values[0] : null
  }

  const getAvailableUnits = (value: string) => {
    return commodities?.filter(c => c.commodityName?.toLowerCase() === value?.toLowerCase()) || []
  }

  useEffect(() => {
    if (xlsFormId && modaFormFields?.length === 0) {
      // Fetch moda form fields only if xlsFormId is available and fields are not already loaded
      setShowCommodities([])
      setShowUnits([])
      setSearchValue("")
      setSearchGroupValue("")
      setFilterType(TypeFilter.ALL)
      setShowCommodities([])
      fetchModaFormFields(xlsFormId)
      setCommonSchema(prev => ({
        ...prev,
        frequency: {
          value: modaForm?.dataCollectionFrequencyId || 0,
          type: "int",
        },
      }))
    }
  }, [xlsFormId, modaFormFields?.length])

  useEffect(() => {
    if (isSuccess && sId) {
      // Update the moda form fields based on the mapping schema
      const assignFieldValue = (value: string): string => {
        if (value === "unit") return "unitName"
        if (value === "price") return "priceValue"

        return value
      }

      setCountry(mappingSchema?.adm0Code || 0)
      setXlsFormId(mappingSchema?.xlsFormId || null)
      fetchModaFormFields(mappingSchema.xlsFormId).then(() => {
        const mappingSchemaJSON = JSON.parse(mappingSchema?.mappingSchemaJSON || "{}")
        Object.keys(mappingSchemaJSON).forEach((key: string) => {
          // Case of schema
          if (key === "schema") {
            const jsonSchema = mappingSchemaJSON[key]
            jsonSchema.map(item => {
              Object.keys(item).map(value => {
                const currentValue: Record<string, unknown> = item[value] as Record<string, unknown>
                const foundField = modaFormFields.find(
                  (field: IFormFieldList) => field?.fieldName === currentValue?.cellValue,
                )
                if (foundField) {
                  // default scenario
                  updateSchema(foundField.fieldIndex, assignFieldValue(value), value)
                  if (item?.commodity) {
                    const currentIndex = foundField.fieldIndex || 0
                    setShowCommodities(prev => [...prev, currentIndex])
                    updateSchema(
                      foundField.fieldIndex,
                      foundField?.assignedTo || "",
                      "commodity",
                      item?.commodity?.value,
                    )
                  }
                  if (item.unit) {
                    const currentIndex = foundField.fieldIndex || 0
                    setShowUnits(prev => [...prev, currentIndex])
                    updateSchema(
                      foundField.fieldIndex,
                      foundField?.assignedTo || "",
                      "unit",
                      foundField?.assignedCommodity || "",
                      item?.unit?.value as string,
                    )
                  }
                }
              })
            })
          } else if (key === "common") {
            const jsonCommon: Record<string, unknown>[] = mappingSchemaJSON[key]
            Object.keys(jsonCommon).map(value => {
              const currentValue: Record<string, unknown> = jsonCommon[value] as Record<string, unknown>
              const foundField = modaFormFields.find(
                (field: IFormFieldList) =>
                  field?.fieldName === currentValue?.cellValue || field?.fieldName === currentValue?.value,
              )
              if (foundField && (currentValue.cellValue || currentValue?.value)) {
                // const itemValue = (currentValue.cellValue || currentValue?.value || "") as string
                updateSchema(foundField.fieldIndex, assignFieldValue(value), value)
              }
              setCommonSchema((prev: any) => ({
                ...prev,
                [value]: {
                  value: currentValue?.value || currentValue?.cellValue || "",
                  type: currentValue?.type,
                },
              }))
            })
          }
        })
      })
    }
  }, [mappingSchema, modaFormFields?.length])

  const renderRow = ({ key, index, style }: ListRowProps): ReactElement => {
    const field: IFormFieldList = filteredFields[index]

    // ---- ROW PER I GRUPPI ----
    if (field.fieldMetadata === "group" || field.fieldMetadata === "root") {
      const hasName = !!field.groupName && field.groupName.trim().length > 0
      const groupLabel = hasName ? field.groupName.trim() : "Unnamed group"
      const isRoot = field.fieldMetadata === "root"

      return (
        <div key={key} style={style} className="px-4 py-3 border-b border-gray-300">
          <div
            style={{
              height: "45px",
              width: "100%",
              borderTop: "2px solid #79797AFF",
              backgroundColor: isRoot ? "#f3f4f6" : "#D4E3FC",
            }}
            className="flex items-center justify-between"
          >
            <div className="flex-1 min-w-0">
              <span className="pl-4 text-sm font-semibold text-gray-900 truncate" title={groupLabel}>
                {`${groupLabel}${field.isRepeatable ? " - Repeatable" : ""}`}
              </span>
            </div>
            {field.isRepeatable && (
              <div className="pr-6">
                <FontAwesomeIcon
                  icon={faRepeat}
                  color="var(--interactive-02)"
                  size="lg"
                  className="cursor-pointer pl-2"
                />
              </div>
            )}
          </div>
        </div>
      )
    }
    // ---- ROW STANDARD (FIELD) ----
    return (
      <div
        key={key}
        style={{ ...style, borderBottom: "2px dashed #999" }}
        className="flex items-center justify-between px-4 py-3 border-b border-b-gray-500 bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex flex-col min-w-0 w-50 mr-4 pl-10">
          <span className="text-sm font-semibold text-gray-900 truncate" title={field.fieldName}>
            {field.fieldName} <span className="text-xs">{field?.fieldTypeLabel && `(${field.fieldTypeLabel})`}</span>
          </span>
          <span className="text-xs text-gray-600 italic truncate" title={field.fieldLabel}>
            {field.fieldLabel || "No label available"}
          </span>
        </div>
        <div className="flex justify-end w-50 gap-2 px-4">
          {DATE_TYPE_VALUES.includes(field?.fieldType) ? (
            <Select
              placeholder="Date"
              className="w-fit min-w-[250px]"
              options={[{ value: "date", label: "Date" }]}
              menuPlacement="top"
              menuPosition="absolute"
              isClearable
              value={field?.assignedTo ? { value: "date", label: "Date" } : null}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999, fontSize: "11px" }),
                control: base => ({ ...base, fontSize: "12px" }),
              }}
              menuPortalTarget={document.body}
              onChange={selectedOption => {
                updateSchema(field.fieldIndex, selectedOption?.value, "date", undefined, undefined, field?.assignedType)
              }}
            />
          ) : (
            <Select
              classNamePrefix="select"
              className="w-fit min-w-[250px]"
              options={modaMappingOptions}
              placeholder="Select"
              menuPlacement="top"
              menuPosition="absolute"
              isClearable
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999, fontSize: "11px" }),
                control: base => ({ ...base, fontSize: "12px" }),
              }}
              value={getDefaultTypeOptions(field?.assignedTo || "")}
              onChange={selectedOption => {
                updateSchema(
                  field.fieldIndex,
                  selectedOption?.value,
                  selectedOption?.type,
                  field?.assignedCommodity,
                  field?.assignedUnit,
                  field?.assignedType,
                )
                if (selectedOption?.commodity && field.fieldIndex) {
                  const currentIndex = field.fieldIndex || 0
                  setShowCommodities(prev => [...prev, currentIndex])
                  if (selectedOption?.unit) setShowUnits(prev => [...prev, currentIndex])
                } else {
                  setShowCommodities(prev => prev.filter(id => id !== field.fieldIndex))
                  setShowUnits(prev => prev.filter(id => id !== field.fieldIndex))
                }
              }}
            />
          )}

          {showCommodities.includes(field?.fieldIndex || 0) && (
            <Select
              placeholder="Commodity"
              options={commodities ?? []}
              className="w-fit min-w-3/12"
              menuPlacement="top"
              menuPosition="absolute"
              menuPortalTarget={document.body}
              getOptionLabel={option => option.commodityName}
              getOptionValue={option => option.commodityID.toString()}
              value={commodities?.find(c => c?.commodityName === field?.assignedCommodity) || []}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999, fontSize: "11px" }),
                control: base => ({ ...base, fontSize: "12px" }),
              }}
              onChange={item => {
                updateSchema(field.fieldIndex, field?.assignedTo || "", "commodity", item?.commodityName)
              }}
            />
          )}

          {showUnits.includes(field?.fieldIndex || 0) && (
            <Select
              placeholder="Unit"
              options={getAvailableUnits(field?.assignedCommodity || "") || []}
              menuPlacement="top"
              menuPosition="absolute"
              menuPortalTarget={document.body}
              getOptionLabel={option => option.commodityUnitName}
              getOptionValue={option => option.commodityUnitID.toString()}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999, fontSize: "11px" }),
                control: base => ({ ...base, fontSize: "12px" }),
              }}
              value={commodities?.find(c => c?.commodityUnitName === field?.assignedUnit) || []}
              className="w-2/12"
              onChange={item => {
                updateSchema(
                  field.fieldIndex,
                  field?.assignedTo || "",
                  "unit",
                  field?.assignedCommodity || "",
                  item?.commodityUnitName as string,
                )
              }}
            />
          )}

          {field?.assignedType?.includes("metadata") && (
            <TextInput
              id="form-label"
              placeholder="metadata"
              value={field?.assignedCustom || ""}
              onChange={e => {
                updateSchema(field.fieldIndex, "metadata", "metadata", undefined, undefined, undefined, e.target.value)
              }}
              className="custom-text-field"
            />
          )}
        </div>
      </div>
    )
  }

  const renderBody = (): ReactElement => {
    if (modaFormFieldsLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loading small withOverlay={false} />
        </div>
      )
    }

    if (modaFormFieldsError) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 h-full text-center px-4">
          <p className="text-sm text-gray-700">{modaFormFieldsError}</p>
        </div>
      )
    }

    if (!filteredFields.length) {
      const hasQuery = searchValue.trim().length >= 3
      return (
        <div className="flex items-center justify-center h-full text-sm text-gray-600 px-4 text-center">
          {hasQuery ? "No fields match your search." : "No fields available for this form."}
        </div>
      )
    }

    return (
      <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            width={width}
            rowCount={filteredFields.length}
            rowHeight={ROW_HEIGHT}
            rowRenderer={renderRow}
            overscanRowCount={5}
          />
        )}
      </AutoSizer>
    )
  }

  if (!schemaId) {
    return (
      <div className="w-full h-[300px] flex justify-center items-center">
        <Loading active={isLoading} small withOverlay={false} />
      </div>
    )
  }

  return (
    <div className="flex flex-col bg-white overflow-hidden h-full">
      <div className="flex justify-between gap-4">
        <div className="text-left">
          <div className="text-xl font-bold">XLSForm Mapping</div>
          <p className="text-sm text-gray-500">Match MODA fields to the standard price data fields.</p>
        </div>
        <div className="flex flex-col gap-1 justify-end">
          <div className="w-64">
            <TextInput
              id="moda-field-search"
              labelText="Search field"
              hideLabel
              placeholder="Search field name"
              value={searchValue}
              onChange={event => setSearchValue(event.currentTarget.value)}
            />
          </div>
          <span className="text-xs text-gray-500">Type at least 3 letters to filter fields</span>
        </div>
        <div className="flex flex-col gap-1 justify-end">
          <div className="">
            <Select
              classNamePrefix="select"
              className="w-[350px]"
              options={allGroup}
              placeholder="Select group name"
              isSearchable
              isClearable
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
              }}
              onChange={selectedOption => {
                setSearchGroupValue(selectedOption ? selectedOption.value : "")
              }}
            />
          </div>
          <span className="text-xs text-gray-500">Select Group</span>
        </div>
      </div>
      <div>
        <div className="mb-1 w-10/12">
          <InputGroup>
            <RadioButton
              value={TypeFilter.ALL}
              labelText="Show all"
              checked={filterType === TypeFilter.ALL}
              onChange={onFilterChange}
            />
            <RadioButton
              value={TypeFilter.UNMAPPED}
              labelText="Show unmapped"
              checked={filterType === TypeFilter.UNMAPPED}
              onChange={onFilterChange}
            />
            <RadioButton
              value={TypeFilter.MAPPED}
              labelText="Show mapped"
              checked={filterType === TypeFilter.MAPPED}
              onChange={onFilterChange}
            />
          </InputGroup>
        </div>
      </div>
      <div className="flex-1 min-h-0">{renderBody()}</div>
    </div>
  )
}

export default ModaFieldMapping
